import { api } from './client';
import type { Case, CaseDetailResponse, CaseStatus, SalesforceCase, SalesforceStatusInfo, TokenUsageEntry, CaseHistoryEntry } from '../types';

interface CasesListParams {
  status?: CaseStatus | CaseStatus[];
  search?: string;
  limit?: number;
  offset?: number;
}

interface CasesListResponse {
  cases: Case[];
  total: number;
}

interface TranslateResponse {
  job_id: string;
  case_number: string;
  status: string;
}

interface UploadResponse {
  job_id: string;
  status: string;
  case_numbers: string[];
  message: string;
}

export const casesApi = {
  /**
   * Get all cases with optional filters
   */
  list: (params?: CasesListParams): Promise<CasesListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.status) {
      const statuses = Array.isArray(params.status) ? params.status : [params.status];
      statuses.forEach((s) => searchParams.append('status', s));
    }
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString();
    return api.get<CasesListResponse>(`/cases${query ? `?${query}` : ''}`);
  },

  /**
   * Get a single case by case number with prev/next navigation neighbors.
   * Efficient endpoint for case review page - avoids loading all cases.
   */
  getDetail: (caseNumber: string): Promise<CaseDetailResponse> => {
    return api.get<CaseDetailResponse>(`/cases/detail/${caseNumber}`);
  },

  /**
   * Download case files from Salesforce
   */
  download: (caseNumbers: string[]): Promise<{ job_id: string }> => {
    return api.post('/cases/download', { case_numbers: caseNumbers });
  },

  /**
   * Translate a case
   */
  translate: (caseNumber: string): Promise<TranslateResponse> => {
    return api.post<TranslateResponse>(`/cases/${caseNumber}/translate`);
  },

  /**
   * Translate multiple cases
   */
  translateBatch: (caseNumbers: string[]): Promise<{ job_id: string }> => {
    return api.post('/cases/translate', { case_numbers: caseNumbers });
  },

  /**
   * Approve a case translation
   */
  approve: (caseNumber: string, notes?: string): Promise<Case> => {
    return api.post<Case>(`/cases/${caseNumber}/approve`, { notes });
  },

  /**
   * Unapprove a case (send back for review)
   */
  unapprove: (caseNumber: string): Promise<Case> => {
    return api.post<Case>(`/cases/${caseNumber}/unapprove`);
  },

  /**
   * Manually change case status
   */
  changeStatus: (caseNumber: string, status: string): Promise<Case> => {
    return api.patch<Case>(`/cases/${caseNumber}/status`, { status });
  },

  /**
   * Upload cases to Salesforce (single or batch)
   */
  upload: (caseNumbers: string[]): Promise<UploadResponse> => {
    return api.post<UploadResponse>('/cases/upload', { case_numbers: caseNumbers });
  },

  /**
   * Get case translation HTML content
   */
  getTranslationHtml: (caseNumber: string): Promise<{ html: string }> => {
    return api.get<{ html: string }>(`/cases/${caseNumber}/translation`);
  },

  /**
   * Update case translation HTML content
   */
  updateTranslationHtml: (caseNumber: string, html: string): Promise<Case> => {
    return api.put<Case>(`/cases/${caseNumber}/translation`, { html });
  },

  /**
   * Regenerate PDF for a case
   */
  regeneratePdf: (caseNumber: string): Promise<{ message: string; status: string }> => {
    return api.post<{ message: string; status: string }>(`/cases/${caseNumber}/regenerate-pdf`);
  },

  /**
   * Get presigned URL for the translated PDF
   */
  getTranslatedPdfUrl: (caseNumber: string): Promise<{ url: string }> => {
    return api.get<{ url: string }>(`/cases/${caseNumber}/translated-pdf-url`);
  },

  /**
   * Get presigned URL for the Word document
   */
  getWordUrl: (caseNumber: string): Promise<{ url: string }> => {
    return api.get<{ url: string }>(`/cases/${caseNumber}/word-url`);
  },

  /**
   * Refresh cases from Salesforce
   */
  refresh: (): Promise<CasesListResponse> => {
    return api.post<CasesListResponse>('/cases/refresh');
  },

  /**
   * Fetch new cases from Salesforce queue (not yet in local DB)
   */
  fetchFromSalesforce: (params?: {
    status?: string;
    unit?: string;
    limit?: number;
  }): Promise<SalesforceCase[]> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status_filter', params.status);
    if (params?.unit) searchParams.set('unit', params.unit);
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    return api.get<SalesforceCase[]>(`/cases/salesforce${query ? `?${query}` : ''}`);
  },

  /**
   * Get presigned URL for the original source PDF
   */
  getOriginalPdfUrl: (caseNumber: string): Promise<{ url: string }> => {
    return api.get<{ url: string }>(`/cases/${caseNumber}/original-url`);
  },

  /**
   * Get case history (status changes and HTML edits)
   */
  getHistory: (caseNumber: string): Promise<CaseHistoryEntry[]> => {
    return api.get<CaseHistoryEntry[]>(`/cases/${caseNumber}/history`);
  },

  /**
   * Restart a case - delete files and re-download from Salesforce
   */
  restart: (caseNumber: string): Promise<{ job_id: string; case_numbers: string[] }> => {
    return api.post(`/cases/${caseNumber}/restart`);
  },

  /**
   * Get current Salesforce status for cases (ephemeral, NOT persisted)
   */
  getSalesforceStatus: (caseNumbers: string[]): Promise<SalesforceStatusInfo[]> => {
    const query = caseNumbers.join(',');
    return api.get<SalesforceStatusInfo[]>(`/cases/salesforce-status?case_numbers=${encodeURIComponent(query)}`);
  },

  /**
   * Get token usage history for a case (all translation attempts)
   */
  getTokenUsage: (caseNumber: string): Promise<TokenUsageEntry[]> => {
    return api.get<TokenUsageEntry[]>(`/cases/${caseNumber}/token-usage`);
  },

  /**
   * Get dashboard statistics (counts by status group)
   * Lightweight endpoint for sidebar badge counts
   */
  getDashboardStats: (): Promise<DashboardStats> => {
    return api.get<DashboardStats>('/cases/stats/dashboard');
  },

  /**
   * Get quick stats for dashboard header
   */
  getQuickStats: (): Promise<QuickStats> => {
    return api.get<QuickStats>('/cases/stats/quick');
  },

  /**
   * Get paginated uploaded cases (server-side pagination)
   */
  getUploadedCases: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<PaginatedUploadedCasesResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.pageSize) searchParams.set('page_size', params.pageSize.toString());
    if (params.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return api.get<PaginatedUploadedCasesResponse>(`/cases/uploaded${query ? `?${query}` : ''}`);
  },

  /**
   * Get paginated 'other' (ignored) cases (server-side pagination)
   */
  getOtherCases: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<PaginatedUploadedCasesResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.pageSize) searchParams.set('page_size', params.pageSize.toString());
    if (params.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return api.get<PaginatedUploadedCasesResponse>(`/cases/other${query ? `?${query}` : ''}`);
  },
};

export interface DashboardStats {
  queue: number;
  approved: number;
  uploaded: number;
  returned: number;
  issues: number;
  other: number;
}

export interface PaginatedUploadedCasesResponse {
  cases: Case[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface QuickStats {
  today_completed: number;
  week_completed: number;
  processing: number;
  errors: number;
}
